import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { 
  PurchaseOrder, 
  PurchaseOrderPriority, 
  CreatePurchaseOrderRequest, 
  CreatePurchaseOrderItemRequest 
} from '../../../models/PurchaseOrder';
import { PurchaseOrderService } from '../../../services/purchase-order.service';
import { ErrorService } from '../../../shared/services/error.service';
import { forkJoin, Observable, of } from 'rxjs';
import { SuppliersService } from '../../../services/suppliers.service';
import { CostCentersService } from '../../../services/cost-centers.service';
import { WarehousesService } from '../../../services/warehouses.service';
import { ViaCepService, ViaCepResponse } from '../../../services/viacep.service';
import { StatusService } from '../../../services/status.service';
import { ItemService } from '../../../services/item.service';
import { AuthService } from '../../../services/auth.service';
import { DepartmentService } from '../../../services/department.service';
import { Supplier } from '../../../models/Supplier';
import { CostCenter } from '../../../models/CostCenter';
import { Warehouse } from '../../../models/Warehouse';
import { Status } from '../../../models/Status';
import { Item } from '../../../models/Item';
import { catchError, map } from 'rxjs/operators';

@Component({
  selector: 'app-purchase-order-form',
  templateUrl: './purchase-order-form.component.html',
  styleUrls: ['./purchase-order-form.component.css']
})
export class PurchaseOrderFormComponent implements OnInit {
  // Formulários
  formStep1!: FormGroup; // Informações básicas
  formStep2!: FormGroup; // Relacionamentos
  formStep3!: FormGroup; // Entrega e pagamento
  formStep4!: FormGroup; // Itens do pedido

  availableTabs = [
    { id: 'basic', label: 'Informações Básicas', icon: 'fas fa-info-circle', required: true, completed: false },
    { id: 'relationships', label: 'Relacionamentos', icon: 'fas fa-link', required: true, completed: false },
    { id: 'delivery', label: 'Entrega e Pagamento', icon: 'fas fa-truck', required: false, completed: false },
    { id: 'items', label: 'Itens do Pedido', icon: 'fas fa-list', required: true, completed: false }
  ];
  activeTab = 'basic';
  purchaseOrderId?: number;
  isSaving = false;
  isEditMode = false;
  isLoading = false;
  error: string | null = null;
  private baseRoute = '/purchase-orders';

  // Dados relacionados
  suppliers: Supplier[] = [];
  costCenters: CostCenter[] = [];
  warehouses: Warehouse[] = [];
  items: Item[] = []; // Itens cadastrados (produtos do estoque)
  staffMembers: any[] = [];
  isCepLoading = false;
  purchaseOrder: PurchaseOrder | null = null;

  // Status
  loadingStatuses = false;
  statusOptions: Status[] = [];

  // Opções para selects
  priorityOptions = [
    { value: PurchaseOrderPriority.BAIXA, label: 'Baixa' },
    { value: PurchaseOrderPriority.NORMAL, label: 'Normal' },
    { value: PurchaseOrderPriority.ALTA, label: 'Alta' },
    { value: PurchaseOrderPriority.URGENTE, label: 'Urgente' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private purchaseOrderService: PurchaseOrderService,
    private errorService: ErrorService,
    private suppliersService: SuppliersService,
    private costCentersService: CostCentersService,
    private warehousesService: WarehousesService,
    private viaCepService: ViaCepService,
    private statusService: StatusService,
    private itemService: ItemService,
    private authService: AuthService,
    private departmentService: DepartmentService
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    this.baseRoute = this.router.url.startsWith('/purchase-requests')
      ? '/purchase-requests'
      : '/purchase-orders';
    this.loadSupportData();
    this.loadPurchaseOrderStatuses();
    
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.purchaseOrderId = +params['id'];
        this.isEditMode = true;
        this.loadPurchaseOrder();
      } else {
        this.generateOrderNumber();
        this.applyDefaultStatusForNewPurchaseOrder();
        // Adicionar um item inicial para novo pedido
        this.addItem();
      }
    });
  }

  get isSolicitacaoComprasFlow(): boolean {
    return this.baseRoute === '/purchase-requests';
  }

  get formPageTitle(): string {
    if (this.isEditMode) {
      return this.isSolicitacaoComprasFlow ? 'Editar Solicitação de Compra' : 'Editar Pedido de Compra';
    }
    return this.isSolicitacaoComprasFlow ? 'Nova Solicitação de Compra' : 'Novo Pedido de Compra';
  }

  get formPageSubtitle(): string {
    if (this.isEditMode) {
      return this.isSolicitacaoComprasFlow
        ? 'Atualize as informações da solicitação de compra'
        : 'Atualize as informações do pedido de compra';
    }
    return this.isSolicitacaoComprasFlow
      ? 'Cadastre uma nova solicitação de compra'
      : 'Cadastre um novo pedido de compra';
  }

  initializeForms(): void {
    this.formStep1 = this.fb.group({
      orderNumber: ['', [Validators.required, Validators.maxLength(50)]],
      priority: [PurchaseOrderPriority.NORMAL, [Validators.required]],
      status: ['Solicitado'], // Novo pedido deve abrir com status "Solicitado"
      expectedDeliveryDate: [''],
      description: ['', [Validators.maxLength(500)]]
    });

    this.formStep2 = this.fb.group({
      supplierId: [0],
      costCenterId: ['', Validators.required],
      deliveryWarehouseId: ['']
    });

    this.formStep3 = this.fb.group({
      zipCode: ['', [Validators.maxLength(9)]],
      deliveryAddress: ['', [Validators.maxLength(100)]],
      paymentTerms: ['', [Validators.maxLength(100)]],
      deliveryInstructions: ['', [Validators.maxLength(500)]],
      notes: ['', [Validators.maxLength(1000)]]
    });

    this.formStep4 = this.fb.group({
      items: this.fb.array([])
    });

    // Itens serão adicionados no ngOnInit baseado no modo
  }

  loadSupportData(): void {
    this.isLoading = true;
    forkJoin({
      suppliers: this.suppliersService.getActive(),
      costCenters: this.costCentersService.getActive(),
      warehouses: this.warehousesService.getActive(),
      items: this.itemService.getActiveItems({ pageSize: 500 })
    }).subscribe({
      next: ({ suppliers, costCenters, warehouses, items }) => {
        this.suppliers = suppliers || [];
        this.costCenters = costCenters.items || [];
        this.warehouses = warehouses || [];
        this.items = items?.items || [];
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar dados de apoio do Pedido de Compra:', error);
        this.errorService.handleHttpError(error);
        this.isLoading = false;
      }
    });
  }

  lookupCep(): void {
    const cepRaw = (this.formStep3.get('zipCode')?.value || '').toString();
    const cep = cepRaw.replace(/\D+/g, '').slice(0, 8);
    if (!cep || cep.length < 8) {
      this.errorService.showErrorMessage('Informe um CEP válido (8 dígitos).');
      return;
    }
    this.isCepLoading = true;
    this.viaCepService.lookup(cep).subscribe({
      next: (res: ViaCepResponse) => {
        if ((res as any).erro) {
          this.errorService.showErrorMessage('CEP não encontrado.');
          this.isCepLoading = false;
          return;
        }
        const address = this.formatCepAddress(res);
        this.formStep3.patchValue({
          deliveryAddress: address,
          zipCode: res.cep
        });
        this.isCepLoading = false;
      },
      error: (err) => {
        console.error('Erro ao buscar CEP:', err);
        this.errorService.handleHttpError(err);
        this.isCepLoading = false;
      }
    });
  }

  private formatCepAddress(res: ViaCepResponse): string {
    const parts = [res.logradouro, res.bairro, `${res.localidade} - ${res.uf}`].filter(Boolean);
    return parts.join(', ');
  }

  loadPurchaseOrderStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('PurchaseOrder', true)
      .subscribe({
        next: (statuses: Status[]) => {
          this.statusOptions = statuses;
          this.ensureSolicitadoStatusOptionForNewPurchaseOrder();
          this.applyDefaultStatusForNewPurchaseOrder();
          this.loadingStatuses = false;
        },
        error: (err) => {
          console.error('Erro ao carregar status de pedidos de compra:', err);
          this.loadingStatuses = false;
          // Fallback para status básicos em caso de erro
          this.statusOptions = [
            { id: 1, name: 'Pendente', description: 'Pedido pendente', entityType: 'PurchaseOrder', isActive: true },
            { id: 2, name: 'Aprovado', description: 'Pedido aprovado', entityType: 'PurchaseOrder', isActive: true },
            { id: 3, name: 'Rejeitado', description: 'Pedido rejeitado', entityType: 'PurchaseOrder', isActive: true },
            { id: 4, name: 'Em Andamento', description: 'Pedido em andamento', entityType: 'PurchaseOrder', isActive: true },
            { id: 5, name: 'Concluído', description: 'Pedido concluído', entityType: 'PurchaseOrder', isActive: true },
            { id: 6, name: 'Cancelado', description: 'Pedido cancelado', entityType: 'PurchaseOrder', isActive: true }
          ] as Status[];
          this.ensureSolicitadoStatusOptionForNewPurchaseOrder();
          this.applyDefaultStatusForNewPurchaseOrder();
        }
      });
  }

  private ensureSolicitadoStatusOptionForNewPurchaseOrder(): void {
    if (this.isEditMode) return;

    const hasSolicitado = this.statusOptions.some(
      status => (status.name || '').trim().toLowerCase() === 'solicitado'
    );

    if (!hasSolicitado) {
      this.statusOptions = [
        {
          id: 0,
          name: 'Solicitado',
          description: 'Pedido solicitado',
          entityType: 'PurchaseOrder',
          isActive: true
        } as Status,
        ...this.statusOptions
      ];
    }
  }

  private applyDefaultStatusForNewPurchaseOrder(): void {
    if (this.isEditMode) return;
    this.formStep1.patchValue({ status: 'Solicitado' }, { emitEvent: false });
  }

  loadPurchaseOrder(): void {
    if (!this.purchaseOrderId) return;

    this.isLoading = true;
    this.purchaseOrderService.getPurchaseOrderById(this.purchaseOrderId).subscribe({
      next: (purchaseOrder) => {
        this.purchaseOrder = purchaseOrder;
        this.populateForm();
        // Na edição, status é obrigatório; na criação o backend define "Solicitado"
        this.formStep1.get('status')?.setValidators(this.isEditMode ? [Validators.required] : []);
        this.formStep1.get('status')?.updateValueAndValidity();
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Erro ao carregar pedido de compra:', error);
        this.error = 'Erro ao carregar pedido de compra';
        this.isLoading = false;
      }
    });
  }

  populateForm(): void {
    if (!this.purchaseOrder) return;

    const po = this.purchaseOrder;

    this.formStep1.patchValue({
      orderNumber: po.orderNumber,
      priority: po.priority,
      status: po.status,
      expectedDeliveryDate: this.formatDateForInput(po.expectedDeliveryDate),
      description: po.description
    });

    this.formStep2.patchValue({
      supplierId: po.supplierId ?? 0,
      costCenterId: po.costCenterId,
      deliveryWarehouseId: po.deliveryWarehouseId
    });

    this.formStep3.patchValue({
      deliveryAddress: po.deliveryAddress,
      paymentTerms: po.paymentTerms,
      deliveryInstructions: po.deliveryInstructions,
      notes: po.notes
    });

    // Popular itens
    this.populateItems(po.items || []);
  }

  populateItems(items: any[]): void {
    // Limpar itens existentes
    while (this.itemsFormArray.length !== 0) {
      this.itemsFormArray.removeAt(0);
    }

    // Se não há itens, adicionar um item vazio
    if (!items || items.length === 0) {
      this.addItem();
      return;
    }

    // Adicionar cada item do pedido de compra
    items.forEach((item: any) => {
      const itemId = item.itemId ?? item.productId ?? null;
      const itemExpectedDate = item.expectedDeliveryDate
        ? this.formatDateForInput(item.expectedDeliveryDate)
        : '';
      const itemForm = this.fb.group({
        itemId: [itemId],
        productName: [item.productName || '', [Validators.required, Validators.maxLength(100)]],
        productCode: [item.productCode || '', [Validators.maxLength(50)]],
        brand: [item.brand || '', [Validators.maxLength(50)]],
        model: [item.model || '', [Validators.maxLength(50)]],
        specifications: [item.specifications || '', [Validators.maxLength(200)]],
        quantityRequested: [item.quantityRequested || 1, [Validators.required, Validators.min(0.01)]],
        unit: [item.unit || 'Unidade', [Validators.required, Validators.maxLength(20)]],
        estimatedUnitPrice: [item.estimatedUnitPrice || 0, [Validators.min(0)]],
        expectedDeliveryDate: [itemExpectedDate],
        description: [item.description || '', [Validators.maxLength(500)]],
        justification: [item.justification || '', [Validators.maxLength(200)]],
        priority: [item.priority || 'Normal', [Validators.maxLength(20)]],
        category: [item.category || '', [Validators.maxLength(50)]],
        isUrgent: [item.isUrgent === true],
        isRecurring: [item.isRecurring === true]
      });

      this.itemsFormArray.push(itemForm);
    });
  }

  // ===== Máscaras e Utilitários =====
  onZipCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskCEP(input.value || '');
    this.formStep3.get('zipCode')?.setValue(masked, { emitEvent: false });
  }

  private onlyDigits(value: string | undefined): string {
    return (value || '').replace(/\D+/g, '');
  }

  private maskCEP(value: string): string {
    let v = this.onlyDigits(value).slice(0, 8);
    if (v.length > 5) {
      v = v.replace(/^(\d{5})(\d)/, '$1-$2');
    }
    return v;
  }

  formatDateForInput(date: string | Date | undefined): string {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toISOString().split('T')[0];
  }

  generateOrderNumber(): void {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    
    const orderNumber = `PC${year}${month}${day}${random}`;
    this.formStep1.patchValue({ orderNumber });
  }

  // Getters para facilitar acesso aos FormArrays
  get itemsFormArray(): FormArray {
    return this.formStep4.get('items') as FormArray;
  }

  selectTab(tabId: string): void {
    if (this.activeTab === tabId) return;
    if (this.availableTabs.some(tab => tab.id === tabId)) {
      this.activeTab = tabId;
    }
  }

  getCurrentTabIndex(): number {
    return this.availableTabs.findIndex(tab => tab.id === this.activeTab);
  }

  isFirstTab(): boolean {
    return this.getCurrentTabIndex() === 0;
  }

  isLastTab(): boolean {
    return this.getCurrentTabIndex() === this.availableTabs.length - 1;
  }

  goToNextTab(): void {
    const form = this.getFormByTabId(this.activeTab);
    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }

    this.markTabAsCompleted(this.activeTab);

    const currentIndex = this.getCurrentTabIndex();
    if (currentIndex < this.availableTabs.length - 1) {
      this.activeTab = this.availableTabs[currentIndex + 1].id;
    }
  }

  goToPreviousTab(): void {
    const currentIndex = this.getCurrentTabIndex();
    if (currentIndex > 0) {
      this.activeTab = this.availableTabs[currentIndex - 1].id;
    }
  }

  isTabValid(tabId: string): boolean {
    switch (tabId) {
      case 'basic':
        return this.formStep1.valid;
      case 'relationships':
        return this.formStep2.valid;
      case 'delivery':
        return this.formStep3.valid;
      case 'items':
        return this.formStep4.valid;
      default:
        return false;
    }
  }

  private markTabAsCompleted(tabId: string): void {
    const tab = this.availableTabs.find(item => item.id === tabId);
    if (tab) {
      tab.completed = true;
    }
  }

  isFormValid(): boolean {
    return this.formStep1.valid && this.formStep2.valid && this.formStep3.valid && this.formStep4.valid;
  }

  // Validação de campos
  isFieldInvalid(fieldName: string, stepNumber: number): boolean {
    const form = this.getFormByStep(stepNumber);
    const field = form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string, stepNumber: number): string {
    const form = this.getFormByStep(stepNumber);
    const field = form.get(fieldName);
    
    if (field?.errors) {
      if (field.errors['required']) return 'Este campo é obrigatório';
      if (field.errors['maxlength']) return `Máximo ${field.errors['maxlength'].requiredLength} caracteres`;
      if (field.errors['min']) return `Valor mínimo: ${field.errors['min'].min}`;
    }
    return '';
  }

  getFormByStep(stepNumber: number): FormGroup {
    switch (stepNumber) {
      case 1: return this.formStep1;
      case 2: return this.formStep2;
      case 3: return this.formStep3;
      case 4: return this.formStep4;
      default: return this.formStep1;
    }
  }

  private getFormByTabId(tabId: string): FormGroup {
    switch (tabId) {
      case 'basic':
        return this.formStep1;
      case 'relationships':
        return this.formStep2;
      case 'delivery':
        return this.formStep3;
      case 'items':
        return this.formStep4;
      default:
        return this.formStep1;
    }
  }

  // Gestão de itens (produtos = itens cadastrados)
  addItem(): void {
    const defaultDate = this.formStep1.get('expectedDeliveryDate')?.value || '';
    const itemForm = this.fb.group({
      itemId: [null as number | null],
      productName: ['', [Validators.required, Validators.maxLength(100)]],
      productCode: ['', [Validators.maxLength(50)]],
      brand: ['', [Validators.maxLength(50)]],
      model: ['', [Validators.maxLength(50)]],
      specifications: ['', [Validators.maxLength(200)]],
      quantityRequested: [1, [Validators.required, Validators.min(0.01)]],
      unit: ['Unidade', [Validators.required, Validators.maxLength(20)]],
      estimatedUnitPrice: [0, [Validators.min(0)]],
      expectedDeliveryDate: [defaultDate],
      description: ['', [Validators.maxLength(500)]],
      justification: ['', [Validators.maxLength(200)]],
      priority: ['Normal', [Validators.maxLength(20)]],
      category: ['', [Validators.maxLength(50)]],
      isUrgent: [false],
      isRecurring: [false]
    });

    this.itemsFormArray.push(itemForm);
  }

  /**
   * Ao selecionar um item cadastrado, preenche nome, código, unidade e preço
   */
  onItemSelected(rowIndex: number, itemId: number | string): void {
    const id = typeof itemId === 'string' ? parseInt(itemId, 10) : itemId;
    if (!id || isNaN(id)) return;

    const item = this.items.find(i => i.id === id);
    if (!item) return;

    const row = this.itemsFormArray.at(rowIndex);
    row.patchValue({
      itemId: item.id,
      productName: item.name,
      productCode: item.code || '',
      brand: item.manufacturerName || '',
      unit: item.unit || 'UN',
      estimatedUnitPrice: item.costPrice ?? item.unitCost ?? 0,
      description: item.description || ''
    }, { emitEvent: true });
  }

  removeItem(index: number): void {
    if (this.itemsFormArray.length > 1) {
      this.itemsFormArray.removeAt(index);
    }
  }

  duplicateItem(index: number): void {
    const existingItem = this.itemsFormArray.at(index).value;
    const newItemForm = this.fb.group({
      itemId: [existingItem.itemId],
      productName: [existingItem.productName, [Validators.required, Validators.maxLength(100)]],
      productCode: [existingItem.productCode, [Validators.maxLength(50)]],
      brand: [existingItem.brand, [Validators.maxLength(50)]],
      model: [existingItem.model, [Validators.maxLength(50)]],
      specifications: [existingItem.specifications, [Validators.maxLength(200)]],
      quantityRequested: [existingItem.quantityRequested, [Validators.required, Validators.min(0.01)]],
      unit: [existingItem.unit, [Validators.required, Validators.maxLength(20)]],
      estimatedUnitPrice: [existingItem.estimatedUnitPrice, [Validators.min(0)]],
      expectedDeliveryDate: [existingItem.expectedDeliveryDate],
      description: [existingItem.description, [Validators.maxLength(500)]],
      justification: [existingItem.justification, [Validators.maxLength(200)]],
      priority: [existingItem.priority || 'Normal', [Validators.maxLength(20)]],
      category: [existingItem.category, [Validators.maxLength(50)]],
      isUrgent: [existingItem.isUrgent === true],
      isRecurring: [existingItem.isRecurring === true]
    });

    this.itemsFormArray.insert(index + 1, newItemForm);
  }

  hasItemFieldError(itemIndex: number, fieldName: string): boolean {
    const field = this.itemsFormArray.at(itemIndex).get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  calculateItemTotal(index: number): number {
    const item = this.itemsFormArray.at(index).value;
    const quantity = item.quantityRequested || 0;
    const price = item.estimatedUnitPrice || 0;
    return quantity * price;
  }

  calculateOrderTotal(): number {
    let total = 0;
    for (let i = 0; i < this.itemsFormArray.length; i++) {
      total += this.calculateItemTotal(i);
    }
    return total;
  }

  // Submissão do formulário
  save(): void {
    if (!this.isFormValid() || this.isSaving) return;

    this.isSaving = true;
    this.error = null;

    const formData = this.prepareFormData();
    if (this.isEditMode) {
      this.submitSaveRequest(formData);
      return;
    }

    this.resolveRequesterStaffIdForCreation(formData.costCenterId).subscribe({
      next: (managerStaffId) => {
        const createPayload: CreatePurchaseOrderRequest = {
          ...formData,
          requestedByStaffId: managerStaffId
        };
        this.submitSaveRequest(createPayload);
      },
      error: (error) => {
        console.error('Erro ao definir gestor solicitante do pedido:', error);
        this.errorService.handleHttpError(error);
        this.isSaving = false;
      }
    });
  }

  private submitSaveRequest(payload: CreatePurchaseOrderRequest): void {
    const request = this.isEditMode
      ? this.purchaseOrderService.updatePurchaseOrder(this.purchaseOrderId!, payload)
      : this.purchaseOrderService.createPurchaseOrder(payload);

    request.subscribe({
      next: () => {
        this.errorService.showInfoMessage(
          this.isEditMode ? 'Pedido de compra atualizado com sucesso!' : 'Pedido de compra criado com sucesso!'
        );
        this.isSaving = false;
        this.router.navigate([this.baseRoute]);
      },
      error: (error: any) => {
        console.error('Erro ao salvar pedido de compra:', error);
        this.errorService.handleHttpError(error);
        this.isSaving = false;
      }
    });
  }

  private resolveRequesterStaffIdForCreation(costCenterId?: number): Observable<number> {
    const fallbackStaffId = this.authService.getStaffIdFromToken() ?? 0;

    if (!costCenterId) {
      return of(fallbackStaffId);
    }

    const costCenter = this.costCenters.find(center => center.id === costCenterId);
    const departmentName = (costCenter?.department || '').trim();
    if (!departmentName) {
      this.errorService.showInfoMessage('Centro de custo sem departamento definido. Solicitante assumido pelo usuário logado.');
      return of(fallbackStaffId);
    }

    return this.departmentService.getDepartmentsPaginated(1, 200, departmentName).pipe(
      map(response => {
        const department = (response.departments || []).find(dep =>
          (dep.name || '').trim().toLowerCase() === departmentName.toLowerCase()
        );
        const managerStaffId = department?.managerStaffId ?? null;

        if (managerStaffId && managerStaffId > 0) {
          return managerStaffId;
        }

        this.errorService.showInfoMessage(
          `Departamento "${departmentName}" sem gestor vinculado. Solicitante assumido pelo usuário logado.`
        );
        return fallbackStaffId;
      }),
      catchError(() => {
        this.errorService.showInfoMessage('Não foi possível validar o gestor da área. Solicitante assumido pelo usuário logado.');
        return of(fallbackStaffId);
      })
    );
  }

  /**
   * Converte valor do formulário para number ou undefined (backend espera int?, não string vazia).
   * Retorna undefined quando vazio para que o JSON não envie a chave ou envie null.
   */
  private toNumberOrUndefined(value: any): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const n = Number(value);
    return isNaN(n) ? undefined : n;
  }

  prepareFormData(): CreatePurchaseOrderRequest {
    const step1Data = this.formStep1.value;
    const step2Data = this.formStep2.value;
    const step3Data = this.formStep3.value;
    const step4Data = this.formStep4.value;

    const expectedOrderDate = step1Data.expectedDeliveryDate
      ? (typeof step1Data.expectedDeliveryDate === 'string'
          ? step1Data.expectedDeliveryDate
          : (step1Data.expectedDeliveryDate as Date).toISOString().split('T')[0])
      : '';

    const items: CreatePurchaseOrderItemRequest[] = step4Data.items.map((item: any) => {
      const qty = Number(item.quantityRequested);
      const price = item.estimatedUnitPrice != null && item.estimatedUnitPrice !== '' ? Number(item.estimatedUnitPrice) : 0;
      const itemId = this.toNumberOrUndefined(item.itemId);
      const itemExpectedDate = item.expectedDeliveryDate
        ? (typeof item.expectedDeliveryDate === 'string' ? item.expectedDeliveryDate : (item.expectedDeliveryDate as Date)?.toISOString?.()?.split('T')[0])
        : expectedOrderDate;
      return {
        itemId: itemId ?? undefined,
        productName: item.productName || '',
        productCode: item.productCode || '',
        brand: item.brand || '',
        model: item.model || '',
        specifications: item.specifications || '',
        unit: item.unit || 'Unidade',
        quantityRequested: isNaN(qty) ? 0 : qty,
        estimatedUnitPrice: price,
        expectedDeliveryDate: itemExpectedDate || undefined,
        description: item.description || '',
        justification: item.justification || '',
        priority: item.priority || 'Normal',
        category: item.category || '',
        isUrgent: item.isUrgent === true,
        isRecurring: item.isRecurring === true
      };
    });

    const currentUser = this.authService.currentUserValue as { staffId?: number } | null;
    const requestedByStaffId = currentUser?.staffId ?? 0;

    const payload: CreatePurchaseOrderRequest = {
      orderNumber: step1Data.orderNumber || '',
      requestDate: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
      status: 'Solicitado',
      priority: step1Data.priority,
      requestedByStaffId,
      // Novo fluxo: pedido nasce sem fornecedor definido (cotação define depois)
      supplierId: 0,
      costCenterId: this.toNumberOrUndefined(step2Data.costCenterId),
      deliveryWarehouseId: this.toNumberOrUndefined(step2Data.deliveryWarehouseId),
      expectedDeliveryDate: expectedOrderDate || undefined,
      description: step1Data.description || '',
      deliveryInstructions: step3Data.deliveryInstructions || '',
      paymentTerms: step3Data.paymentTerms || '',
      deliveryAddress: step3Data.deliveryAddress || '',
      supplierOrderNumber: '',
      notes: step3Data.notes || '',
      discountAmount: 0,
      shippingCost: 0,
      taxAmount: 0,
      items
    };

    return payload;
  }

  cancel(): void {
    this.router.navigate([this.baseRoute]);
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }
}
